import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
  Card,
  DatePicker,
  Empty,
  Flex,
  Form,
  FormListFieldData,
  InputNumber,
  Select,
  Spin,
  Table,
  TableColumnsType
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import cronstrue from 'cronstrue'
import dayjs, { Dayjs } from 'dayjs'
import { useMemo, useRef, useState } from 'react'

import {
  useStocksServiceGetInternalInventoryListEndpointKey,
  useTreatmentServiceAddTreatmentPlanPrescribedTreatmentEndpoint,
  useTreatmentServiceUpdateTreatmentPlanPrescribedTreatmentEndpoint,
  useTreatmentServiceDeleteTreatmentPlanPrescribedTreatmentEndpoint,
  useTreatmentServiceGetTreatmentPlanEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetMedicineListResponse,
  GetTreatmentPlanResponse,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { EditableFormItem, message, SubmitButtons, Text } from 'src/components'
import { EXISTING_TREATMENT_PLAN } from 'src/features/persons/utils/constants'
import { useDebounce, useUser } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { TreatmentInquiryItem } from '../types'

import { CronModal } from './CronModal'
import { normalizeCron } from './cronUtils'

import 'cronstrue/locales/ro'
import 'cronstrue/locales/ru'

type Props = {
  fields: FormListFieldData[]
  form?: FormInstance
  remove: (index: number) => void
  isDisabled?: boolean
  data?: GetTreatmentPlanResponse
}

const fieldsToReset = [
  'internationalNameId',
  'name',
  'productFormId',
  'amount',
  'cron',
  'quantity',
  'startDate',
  'endDate'
]

const generateFields = (medicines: { name?: string }[]) =>
  medicines
    .map((_, index: number) =>
      fieldsToReset.map((field) => ['medicine', index, field])
    )
    .flat()

export const TreatmentsConsumablesTables = ({
  fields,
  form,
  remove,
  isDisabled = false,
  data: treatmentPlanData
}: Props) => {
  const [search, setSearch] = useState('')
  const { t, currentLang } = useLocales()
  const { userData } = useUser()
  const [editingIndex, setEditingIndex] = useState<number>()
  const [isNewConsumable, setIsNewConsumable] = useState(false)
  const [isCronModalOpen, setIsCronModalOpen] = useState(false)
  const originalValueRef = useRef<TreatmentInquiryItem | null>(null)

  const debouncedSearch = useDebounce(search)

  const palette = useTheme()
  const queryClient = useQueryClient()

  const treatmentPlanId = form?.getFieldValue(EXISTING_TREATMENT_PLAN)

  const { mutate: addPrescribedTreatment, isPending: isAdding } =
    useTreatmentServiceAddTreatmentPlanPrescribedTreatmentEndpoint({
      onSuccess: (data) => {
        message.success(t('persons.treatment.prescribed_treatment_added'))

        if (editingIndex !== undefined) {
          const medicines = form?.getFieldValue('medicine') || []
          medicines[editingIndex] = {
            ...medicines[editingIndex],
            id: data?.id
          }
          form?.setFieldsValue({ medicine: medicines })
        }

        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanEndpointKey]
        })
        setIsNewConsumable(false)
        setEditingIndex(undefined)
      },
      onError: (error: ApiError) => {
        formErrorHandler(error.body.errors)
      }
    })

  const { mutate: updatePrescribedTreatment, isPending: isUpdating } =
    useTreatmentServiceUpdateTreatmentPlanPrescribedTreatmentEndpoint({
      onSuccess: () => {
        message.success(t('persons.treatment.prescribed_treatment_updated'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanEndpointKey]
        })
        originalValueRef.current = null
        setEditingIndex(undefined)
      },
      onError: (error: ApiError) => {
        message.error(t('common.error_occurred'))
        formErrorHandler(error.body.errors)
      }
    })

  const { mutate: deletePrescribedTreatment, isPending: isDeleting } =
    useTreatmentServiceDeleteTreatmentPlanPrescribedTreatmentEndpoint({
      onSuccess: () => {
        message.success(t('persons.treatment.prescribed_treatment_deleted'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanEndpointKey]
        })
      },
      onError: (error: ApiError) => {
        if (error.status === 400) {
          message.error(t('persons.treatment.cannot_delete_administered'))
        } else {
          message.error(t('common.error_occurred'))
        }
        formErrorHandler(error.body.errors)
      }
    })

  const { data, isFetching, fetchNextPage } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetInternalInventoryListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getInternalInventoryListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        undefined,
        undefined,
        [String(userData?.currentRoleId)]
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetMedicineListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const medicines = data?.pages.flatMap((page) => page.data) || []

  const selectOptions = medicines.map((item) => {
    const prefixes = item?.internationalName?.activeSubstances?.map(
      (item) => item?.prefix
    )

    const prefixLabel = (prefixes ?? [])
      .filter((p) => p?.trim() !== '')
      .join('+')

    return {
      label: `${item?.internationalName?.name} ${prefixLabel ? ` (${prefixLabel})` : ''}`,
      value: item?.id,
      item
    }
  })

  const list = Form.useWatch('medicine', form) || []

  const filteredCombinedData = useMemo(() => {
    const selectedIds = new Set(
      list.map((item: TreatmentInquiryItem) => item.id)
    )

    return selectOptions.filter((data) => !selectedIds.has(data.value))
  }, [selectOptions, list])

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else if (originalValueRef.current) {
      const medicines = form?.getFieldValue('medicine') || []
      medicines[index] = originalValueRef.current
      form?.setFieldsValue({ medicine: medicines })
    }

    originalValueRef.current = null
    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const onSelect = async (value: string) => {
    const medicines = form?.getFieldValue('medicine')

    if (medicines?.length > 0) {
      const fieldsToValidate = generateFields(medicines)

      await form?.validateFields(fieldsToValidate)
    }

    const selectedItem = selectOptions?.find((item) => item.value === value)

    const prefixes =
      selectedItem?.item?.internationalName?.activeSubstances?.map(
        (item) => item?.prefix
      )

    const prefixLabel = (prefixes ?? [])
      .filter((p) => p?.trim() !== '')
      .join('+')

    if (selectedItem) {
      const newInquiryItem = {
        id: selectedItem?.item?.id,
        internationalNameId: selectedItem?.item?.internationalName?.id,
        name: `${selectedItem?.label}${prefixLabel ? ` (${prefixLabel})` : ''}`,
        productFormId: {
          id: selectedItem?.item?.productForm?.id,
          name: selectedItem?.item?.productForm?.name
        },
        item: selectedItem.item,
        cron: undefined
      }

      form?.setFieldsValue({ medicine: [...list, newInquiryItem] })
      setEditingIndex(list.length)
      setIsNewConsumable(true)
      setSearch('')
    }
  }

  const onSave = async () => {
    const fieldsToValidate = fieldsToReset.map((field) => [
      'medicine',
      editingIndex!,
      field
    ])

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      const originalMedicine =
        treatmentPlanData?.prescribedTreatments?.[editingIndex!]
      const currentMedicine = values?.medicine?.[editingIndex!]

      if (treatmentPlanId) {
        const prescribedTreatmentId = originalMedicine?.id

        if (prescribedTreatmentId) {
          updatePrescribedTreatment({
            id: treatmentPlanId,
            ptId: prescribedTreatmentId,
            requestBody: {
              internationalNameId: currentMedicine.internationalNameId,
              productFormId: currentMedicine.productFormId.id,
              amount: currentMedicine.amount,
              scheduleExpression: normalizeCron(currentMedicine.cron) as string,
              startDate: dayjs(currentMedicine.startDate).format(
                yearMonthDaySlash
              ),
              endDate: currentMedicine.endDate
                ? dayjs(currentMedicine.endDate).format(yearMonthDaySlash)
                : null
            }
          })
        } else {
          addPrescribedTreatment({
            id: treatmentPlanId,
            requestBody: {
              internationalNameId: currentMedicine.internationalNameId,
              productFormId: currentMedicine.productFormId.id,
              amount: currentMedicine.amount,
              scheduleExpression: normalizeCron(currentMedicine.cron) as string,
              startDate: dayjs(currentMedicine.startDate).format(
                yearMonthDaySlash
              ),
              endDate: currentMedicine.endDate
                ? dayjs(currentMedicine.endDate).format(yearMonthDaySlash)
                : null
            }
          })
        }
      } else {
        originalValueRef.current = null
        setIsNewConsumable(false)
        setEditingIndex(undefined)
        form?.setFieldsValue({ medicine: values?.medicine })
      }
    }
  }

  const isStartDateValid = (value: Dayjs | null, form?: FormInstance) => {
    const start = form?.getFieldValue('treatment_start_date')
    const end = form?.getFieldValue('treatment_end_date')
    if (!value || !start) return false

    const today = dayjs().startOf('day')
    if (value.isBefore(today, 'day')) {
      return true
    }

    return (
      value.isBefore(dayjs(start), 'day') || value.isAfter(dayjs(end), 'day')
    )
  }

  const isEndDateValid = (value: Dayjs | null, form?: FormInstance) => {
    const medicineStart = form?.getFieldValue([
      'medicine',
      editingIndex,
      'startDate'
    ])
    const start = medicineStart ?? form?.getFieldValue('treatment_start_date')
    const end = form?.getFieldValue('treatment_end_date')
    if (!value || !start || !end) return false

    const min = dayjs(start).startOf('day')
    const max = dayjs(end).endOf('day')

    return value.isBefore(min, 'day') || value.isAfter(max, 'day')
  }

  const onDelete = (index: number) => {
    const medicines = form?.getFieldValue('medicine')
    const currentMedicine = medicines[index]

    if (treatmentPlanId && currentMedicine.id) {
      deletePrescribedTreatment({
        id: treatmentPlanId,
        ptId: currentMedicine.id
      })
    }

    remove(index)
  }

  const columns: TableColumnsType = useMemo(
    () => [
      {
        key: 'name',
        title: t('consultations.active_substance'),
        render: (_, __, index) => {
          const um = form?.getFieldValue(['medicine', index, 'name'])

          return <Text>{um}</Text>
        }
      },

      {
        key: 'um',
        title: t('common.unit'),
        render: (_, __, index) => {
          const um = form?.getFieldValue([
            'medicine',
            index,
            'productFormId',
            'name'
          ])

          return <Text>{um}</Text>
        }
      },
      {
        key: 'amount',
        width: '20%',
        title: t('common.quantity'),
        render: (_, __, index) => {
          return (
            <EditableFormItem
              name={[index, 'amount']}
              isEditing={editingIndex === index}
              rules={requiredRules}
            >
              <InputNumber
                min={1}
                placeholder={t('common.quantity')}
                className="full-width"
              />
            </EditableFormItem>
          )
        }
      },
      {
        key: 'cron',
        title: t('cron.schedule'),
        render: (_, __, index) => {
          const cron = form?.getFieldValue(['medicine', index, 'cron'])

          return (
            <Form.Item name={[index, 'cron']} rules={[{ required: true }]}>
              <Text
                underline={!treatmentPlanId}
                className={treatmentPlanId ? 'default' : 'pointer'}
                onClick={() => {
                  setIsCronModalOpen(true)
                  setEditingIndex(index)
                }}
              >
                {cron
                  ? cronstrue.toString(cron, {
                      locale: currentLang?.value,
                      verbose: true
                    })
                  : t('cron.select_schedule')}
              </Text>
            </Form.Item>
          )
        }
      },
      {
        key: 'startDate',
        title: t('cron.start_data_administration'),
        render: (_, __, index) => {
          return (
            <EditableFormItem
              name={[index, 'startDate']}
              isEditing={editingIndex === index}
              rules={requiredRules}
            >
              <DatePicker
                format={yearMonthDaySlash}
                className="full-width"
                disabled={isDisabled}
                disabledDate={(current) => isStartDateValid(current, form)}
              />
            </EditableFormItem>
          )
        }
      },
      {
        key: 'endDate',
        title: t('cron.end_data_administration'),
        render: (_, __, index) => {
          return (
            <EditableFormItem
              name={[index, 'endDate']}
              isEditing={editingIndex === index}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    if (isEndDateValid(value, form)) {
                      return Promise.reject(
                        new Error(t('persons.treatment.medicine_end_invalid'))
                      )
                    }
                    return Promise.resolve()
                  }
                }
              ]}
            >
              <DatePicker
                format={yearMonthDaySlash}
                className="full-width"
                disabledDate={(current) => isEndDateValid(current, form)}
              />
            </EditableFormItem>
          )
        }
      },

      {
        width: 105,
        fixed: 'right' as const,
        key: 'actions',
        render: (_: unknown, __: unknown, index: number) => (
          <SubmitButtons
            editingIndex={editingIndex}
            isEditing={editingIndex === index}
            onSave={onSave}
            onCancel={() => onCancel(index)}
            onEdit={() => {
              const medicines = form?.getFieldValue('medicine') || []
              originalValueRef.current = { ...medicines[index] }
              setEditingIndex(index)
            }}
            onDelete={() => onDelete(index)}
            isLoading={isAdding || isUpdating || isDeleting}
            disabled={isDisabled}
          />
        )
      }
    ],
    [t, palette]
  )

  return (
    <>
      <Table
        dataSource={fields}
        rowKey="name"
        scroll={{ x: 'max-content' }}
        columns={columns}
        pagination={false}
      />

      {!isDisabled && (
        <Card variant={'borderless'}>
          <Select
            allowClear
            showSearch
            value={null}
            onSearch={(value) => setSearch(value)}
            onClear={() => setSearch('')}
            loading={isFetching}
            options={filteredCombinedData}
            filterOption={false}
            onSelect={(value) => value && onSelect(value)}
            placeholder={t('common.search')}
            onPopupScroll={(e) => {
              const target = e.target as HTMLElement
              if (
                target.scrollTop + target.offsetHeight ===
                target.scrollHeight
              ) {
                fetchNextPage()
              }
            }}
            notFoundContent={
              isFetching ? (
                <Flex justify="center">
                  <Spin spinning />
                </Flex>
              ) : (
                <Empty />
              )
            }
          />
        </Card>
      )}

      {isCronModalOpen && (
        <CronModal
          value={form?.getFieldValue(['medicine', editingIndex, 'cron'])}
          onCancel={() => setIsCronModalOpen(false)}
          onSave={(value) => {
            form?.setFieldValue(['medicine', editingIndex, 'cron'], value)
            setIsCronModalOpen(false)
          }}
        />
      )}
    </>
  )
}
